// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
// Licensed under the Apache License, Version 2.0 (https://www.apache.org/licenses/LICENSE-2.0).

// Result view of a Set_Run or Set_Preview_Run (blueprint-sets Req 3.7, 5.2-5.4, 5.6-5.8, 5.10, 5.11, 6.4-6.9, 7;
// design "components/SetResultView.tsx"). A "Packet summary" tab first, a segment overview, then one tab per Segment
// labeled with its page range and the Studio name of its Matched_Member. The Packet_Summary and both downloads are
// computed in the browser from the stored result, the run's snapshot, the confidence threshold, and the Segments the
// author accepted with "Use this match" (view state only: not stored, not in the URL, reset on reload).
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import Alert from '@cloudscape-design/components/alert';
import Badge from '@cloudscape-design/components/badge';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import Header from '@cloudscape-design/components/header';
import Link from '@cloudscape-design/components/link';
import Select from '@cloudscape-design/components/select';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Spinner from '@cloudscape-design/components/spinner';
import StatusIndicator from '@cloudscape-design/components/status-indicator';
import Table from '@cloudscape-design/components/table';
import Tabs from '@cloudscape-design/components/tabs';
import Toggle from '@cloudscape-design/components/toggle';
import {
  allowedValuesByPath,
  computePacketSummary,
  isPossibleMismatch,
  MISSING_FIELD_MESSAGE,
  resultSegments,
  segmentLabel,
  toSetCsvFiles,
  toSetResultJson,
  type BdaSchema,
  type CheckStatus,
  type FieldValue,
  type NormalizedResult,
  type NormalizedSegment,
  type PacketSummary,
  type SetRunSnapshot,
  type SummaryInput,
} from '@bda/shared';
import { api, ApiError } from '../../api/client';
import { csvDownload, saveBlob, saveText } from '../../lib/download';
import { ErrorDetail } from '../../lib/notify';
import { ConfidenceSlider, isLow, SegmentPanel, type AllowedValues } from '../runs/ResultView';
import { useConfidenceThreshold } from '../runs/confidenceThreshold';

type Member = SetRunSnapshot['members'][number];

/** Which label the view carries: a run ("Draft set" when it used the Set_Draft) or an unsaved set preview. */
export type SetResultLabel = 'run' | 'preview';

export interface SetResultViewProps {
  result: NormalizedResult;
  /** The set snapshot the run used (`runResultResponse.set`). */
  snapshot: SetRunSnapshot;
  /** File name stem of the downloads. */
  baseName: string;
  onShowPage?: (page: number) => void;
  /** `run` (default): "Draft set" badge when `snapshot.setVersion === 'DRAFT'`; `preview`: "Preview: set not saved". */
  label?: SetResultLabel;
  /**
   * Content type of the run's document. PDF pages can become a sample ("Create a blueprint from these pages"); for
   * other types the action is "Create a blueprint from this document". Omitted = PDF (packets are PDFs).
   */
  contentType?: string;
  /**
   * Override of the create-blueprint action for one Segment. Default: `createBlueprintFromSegment` and then
   * `/blueprints/new?sampleRun=<newRunId>` (PDF), or `/blueprints/new` for another document type.
   */
  onCreateBlueprint?: (segmentIndex: number) => Promise<void> | void;
}

export const DRAFT_SET_LABEL = 'Draft set';
export const PREVIEW_SET_LABEL = 'Preview: set not saved';
export const NO_MATCH_LABEL = 'No matching blueprint';
export const POSSIBLE_MISMATCH_LABEL = 'Possible wrong blueprint';
export const CREATE_FROM_PAGES = 'Create a blueprint from these pages';
export const CREATE_FROM_DOCUMENT = 'Create a blueprint from this document';
export const NO_SEGMENT_TEXT = 'BDA wrote no text for these pages.';
const SUMMARY_TAB = 'summary';
const tabId = (index: number) => `segment-${index}`;

/** The Editor path that starts the Start_From_Document_Flow at "BDA is reading the document" for a sample run. */
export const sampleEditorPath = (runId: string): string => `/blueprints/new?sampleRun=${encodeURIComponent(runId)}`;

/**
 * "Create a blueprint from these pages": a new, started suggest run on the Segment's pages of the run's PDF (Req 5.4).
 * Returns the new run's id, for `sampleEditorPath`.
 */
export async function createBlueprintFromSegment(runId: string, segmentIndex: number): Promise<string> {
  const run = await api.createSampleFromPages(runId, segmentIndex);
  return run.id;
}

/** The snapshot Member a Segment matched, or null for a No_Match_Segment. */
const memberOf = (snapshot: SetRunSnapshot, s: NormalizedSegment): Member | null =>
  s.matchedMember === null || s.matchedMember === undefined ? null : (snapshot.members[s.matchedMember] ?? null);

/** Id the Packet_Summary knows a Member by; preview Members without a catalog blueprint get a stable stand-in. */
const memberKey = (m: Member): string => m.blueprintId ?? `#member-${m.index}`;

/** Values in a Segment below the threshold (fields and table cells), as in `segments.csv`. */
const lowCount = (s: NormalizedSegment, threshold: number): number =>
  s.fields.filter((f) => isLow(f.confidence, threshold)).length +
  s.tables.reduce(
    (n, t) => n + t.rows.reduce((m, r) => m + Object.values(r).filter((c) => isLow(c.confidence, threshold)).length, 0),
    0,
  );

const showValue = (v: FieldValue): string =>
  v === null
    ? '-'
    : Array.isArray(v)
      ? v.length === 0
        ? '-'
        : v.map((x) => (typeof x === 'boolean' ? (x ? 'Yes' : 'No') : String(x))).join(', ')
      : typeof v === 'boolean'
        ? v
          ? 'Yes'
          : 'No'
        : String(v);
const showNumber = (n: number | null): string =>
  n === null ? '-' : n.toLocaleString('en-US', { maximumFractionDigits: 4 });
const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
const pct = (c: number | null | undefined): string => (c === null || c === undefined ? '-' : `${Math.round(c * 100)}%`);

const CHECK_STATUS: Record<CheckStatus, { type: 'success' | 'error' | 'warning'; text: string }> = {
  PASS: { type: 'success', text: 'Pass' },
  FAIL: { type: 'error', text: 'Fail' },
  UNVERIFIED: { type: 'warning', text: 'Unverified' },
};
const INPUT_REASON: Record<SummaryInput['reason'], string> = {
  LOW_CONFIDENCE: 'low confidence',
  MISSING: 'missing',
  POSSIBLE_MISMATCH: 'possible wrong blueprint',
};

export function SetResultView({
  result,
  snapshot,
  baseName,
  onShowPage,
  label = 'run',
  contentType,
  onCreateBlueprint,
}: SetResultViewProps) {
  const [threshold, updateThreshold] = useConfidenceThreshold();
  const [accepted, setAccepted] = useState<ReadonlySet<number>>(() => new Set());
  const [activeTab, setActiveTab] = useState(SUMMARY_TAB);
  const [memberFilter, setMemberFilter] = useState('all');
  const [mismatchOnly, setMismatchOnly] = useState(false);

  const segments = useMemo(() => resultSegments(result), [result]);
  const members = useMemo(
    () => snapshot.members.map((m) => ({ blueprintId: memberKey(m), schema: m.schema as unknown as BdaSchema })),
    [snapshot],
  );
  const allowedByMember = useMemo(
    () => snapshot.members.map((m) => allowedValuesByPath(m.schema) as AllowedValues),
    [snapshot],
  );
  const summary = useMemo(
    () =>
      computePacketSummary({
        rules: snapshot.summaryRules,
        members,
        segments,
        threshold,
        coverageFloor: snapshot.coverageFloor,
        acceptedSegments: accepted,
      }),
    [snapshot, members, segments, threshold, accepted],
  );

  /** A Possible_Mismatch_Segment at the snapshot's floor, accepted or not. */
  const flagged = (s: NormalizedSegment) => isPossibleMismatch(s, snapshot.coverageFloor);
  /** Shows the marker: flagged and not accepted in this view. */
  const marked = (s: NormalizedSegment) => flagged(s) && !accepted.has(s.index);

  const visible = segments.filter((s) => {
    if (mismatchOnly && !flagged(s)) return false;
    if (memberFilter === 'all') return true;
    if (memberFilter === 'none') return memberOf(snapshot, s) === null;
    return String(s.matchedMember) === memberFilter && memberOf(snapshot, s) !== null;
  });
  const currentTab =
    activeTab === SUMMARY_TAB || visible.some((s) => tabId(s.index) === activeTab) ? activeTab : SUMMARY_TAB;
  const selectSegment = (index: number) => {
    // A link from the summary always reaches its tab: clear filters that hide it.
    if (!visible.some((s) => s.index === index)) {
      setMemberFilter('all');
      setMismatchOnly(false);
    }
    setActiveTab(tabId(index));
  };

  const setAccept = (index: number, on: boolean) =>
    setAccepted((prev) => {
      const next = new Set(prev);
      if (on) next.add(index);
      else next.delete(index);
      return next;
    });

  const downloadJson = () =>
    saveText(
      JSON.stringify(toSetResultJson({ snapshot, result, summary, acceptedSegments: accepted }), null, 2),
      `${baseName}.json`,
      'application/json',
    );
  const downloadCsv = () => {
    const { blob, fileName } = csvDownload(
      toSetCsvFiles({ snapshot, result, summary, threshold, acceptedSegments: accepted }),
      baseName,
    );
    saveBlob(blob, fileName);
  };

  const matchedMemberIndexes = [
    ...new Set(segments.flatMap((s) => (memberOf(snapshot, s) ? [s.matchedMember!] : []))),
  ].sort((a, b) => a - b);
  const filterOptions = [
    { value: 'all', label: 'All blueprints' },
    ...matchedMemberIndexes.map((i) => ({ value: String(i), label: snapshot.members[i]!.name })),
    ...(segments.some((s) => memberOf(snapshot, s) === null) ? [{ value: 'none', label: NO_MATCH_LABEL }] : []),
  ];

  const tabLabel = (s: NormalizedSegment): string => {
    const m = memberOf(snapshot, s);
    if (!m) return `${segmentLabel(s)} · ${NO_MATCH_LABEL}`;
    return marked(s) ? `${segmentLabel(s)} · ${m.name} · ${POSSIBLE_MISMATCH_LABEL}` : `${segmentLabel(s)} · ${m.name}`;
  };
  const isPdf = contentType === undefined || contentType === 'application/pdf';
  const memberName = (key: string) => snapshot.members.find((m) => memberKey(m) === key)?.name ?? key;

  return (
    <SpaceBetween size="l">
      <SpaceBetween direction="horizontal" size="xs" alignItems="center">
        {label === 'preview' ? (
          <Badge color="blue">{PREVIEW_SET_LABEL}</Badge>
        ) : (
          snapshot.setVersion === 'DRAFT' && <Badge color="grey">{DRAFT_SET_LABEL}</Badge>
        )}
        <Button iconName="download" onClick={downloadJson}>
          Download JSON
        </Button>
        <Button iconName="download" onClick={downloadCsv}>
          Download CSV (ZIP)
        </Button>
      </SpaceBetween>
      <ConfidenceSlider threshold={threshold} onChange={updateThreshold} />
      <Table
        header={
          <Header
            variant="h2"
            counter={`(${visible.length === segments.length ? segments.length : `${visible.length}/${segments.length}`})`}
            actions={
              <SpaceBetween direction="horizontal" size="m" alignItems="center">
                <Select
                  selectedOption={filterOptions.find((o) => o.value === memberFilter) ?? filterOptions[0]!}
                  options={filterOptions}
                  onChange={(e) => setMemberFilter(e.detail.selectedOption.value ?? 'all')}
                  ariaLabel="Filter segments by blueprint"
                />
                <Toggle checked={mismatchOnly} onChange={(e) => setMismatchOnly(e.detail.checked)}>
                  {POSSIBLE_MISMATCH_LABEL} only
                </Toggle>
              </SpaceBetween>
            }
          >
            Segments
          </Header>
        }
        ariaLabels={{ tableLabel: 'Segment overview' }}
        items={visible}
        trackBy={(s) => String(s.index)}
        empty={<Box>No segments match the filter.</Box>}
        columnDefinitions={[
          {
            id: 'pages',
            header: 'Pages',
            isRowHeader: true,
            cell: (s) => (
              <Link
                onFollow={(e) => {
                  e.preventDefault();
                  selectSegment(s.index);
                }}
                ariaLabel={`Show ${tabLabel(s)}`}
              >
                {segmentLabel(s)}
              </Link>
            ),
          },
          {
            id: 'member',
            header: 'Blueprint',
            cell: (s) => memberOf(snapshot, s)?.name ?? <Box color="text-body-secondary">{NO_MATCH_LABEL}</Box>,
          },
          {
            id: 'coverage',
            header: 'Fields found',
            cell: (s) =>
              memberOf(snapshot, s) && s.fieldCoverage
                ? `${s.fieldCoverage.found} of ${plural(s.fieldCoverage.total, 'field')}`
                : '-',
          },
          {
            id: 'marker',
            header: 'Match',
            cell: (s) =>
              marked(s) ? (
                <StatusIndicator type="warning">{POSSIBLE_MISMATCH_LABEL}</StatusIndicator>
              ) : flagged(s) ? (
                <StatusIndicator type="info">Match accepted</StatusIndicator>
              ) : null,
          },
          { id: 'low', header: 'Low confidence', cell: (s) => (memberOf(snapshot, s) ? lowCount(s, threshold) : '-') },
          // Req 5.11: information only; never used to decide whether a Segment is matched.
          {
            id: 'bdaConfidence',
            header: 'BDA match confidence',
            cell: (s) => (memberOf(snapshot, s) ? pct(s.matchedBlueprint?.confidence) : '-'),
          },
        ]}
      />
      <Tabs
        ariaLabel="Set result"
        activeTabId={currentTab}
        onChange={(e) => setActiveTab(e.detail.activeTabId)}
        tabs={[
          {
            id: SUMMARY_TAB,
            label: 'Packet summary',
            content: (
              <PacketSummaryPanel
                summary={summary}
                threshold={threshold}
                segments={segments}
                memberName={memberName}
                onSelectSegment={selectSegment}
              />
            ),
          },
          ...visible.map((s) => {
            const m = memberOf(snapshot, s);
            const allowed =
              s.matchedMember !== null && s.matchedMember !== undefined ? allowedByMember[s.matchedMember] : undefined;
            return {
              id: tabId(s.index),
              label: tabLabel(s),
              content: !m ? (
                <NoMatchPanel
                  runId={result.runId}
                  segment={s}
                  isPdf={isPdf}
                  onShowPage={onShowPage}
                  onCreateBlueprint={onCreateBlueprint}
                />
              ) : flagged(s) ? (
                <MismatchPanel
                  runId={result.runId}
                  segment={s}
                  member={m}
                  accepted={accepted.has(s.index)}
                  onAccept={(on) => setAccept(s.index, on)}
                  threshold={threshold}
                  allowed={allowed}
                  isPdf={isPdf}
                  onShowPage={onShowPage}
                  onCreateBlueprint={onCreateBlueprint}
                />
              ) : (
                <SegmentPanel segment={s} threshold={threshold} onShowPage={onShowPage} allowed={allowed} />
              ),
            };
          }),
        ]}
      />
    </SpaceBetween>
  );
}

// ---------- Packet summary tab ----------

function PacketSummaryPanel({
  summary,
  threshold,
  segments,
  memberName,
  onSelectSegment,
}: {
  summary: PacketSummary;
  threshold: number;
  segments: NormalizedSegment[];
  memberName: (key: string) => string;
  onSelectSegment: (index: number) => void;
}) {
  const hasRules = summary.headers.length + summary.rollups.length + summary.checks.length > 0;
  const segmentOf = (i: number) => segments.find((s) => s.index === i);
  const inputLinks = (inputs: SummaryInput[]) =>
    inputs.length === 0 ? null : (
      <ul className="value-list">
        {inputs.map((inp) => {
          const s = segmentOf(inp.segmentIndex);
          const where = s ? segmentLabel(s) : `Segment ${inp.segmentIndex + 1}`;
          const path = inp.path ? ` ${inp.path}${inp.row !== undefined ? ` (row ${inp.row + 1})` : ''}` : '';
          return (
            <li key={`${inp.segmentIndex}|${inp.path}|${inp.row ?? ''}`}>
              <Link
                onFollow={(e) => {
                  e.preventDefault();
                  onSelectSegment(inp.segmentIndex);
                }}
              >
                {where}
                {path}: {INPUT_REASON[inp.reason]}
              </Link>
            </li>
          );
        })}
      </ul>
    );

  return (
    <SpaceBetween size="l">
      {!hasRules && (
        <Box color="text-body-secondary">
          This set has no packet summary rules. The segment overview lists every segment.
        </Box>
      )}
      {summary.headers.length > 0 && (
        <Table
          header={<Header variant="h3">Header fields</Header>}
          ariaLabels={{ tableLabel: 'Header fields' }}
          items={summary.headers}
          trackBy="id"
          columnDefinitions={[
            { id: 'label', header: 'Field', isRowHeader: true, cell: (h) => h.label },
            {
              id: 'value',
              header: 'Value',
              cell: (h) =>
                h.invalid ? (
                  <StatusIndicator type="error">{MISSING_FIELD_MESSAGE}</StatusIndicator>
                ) : (
                  showValue(h.value)
                ),
            },
            {
              id: 'confidence',
              header: 'Confidence',
              cell: (h) =>
                h.invalid || h.confidence === null ? (
                  '-'
                ) : (
                  <SpaceBetween direction="horizontal" size="xxs">
                    <span>{pct(h.confidence)}</span>
                    {isLow(h.confidence, threshold) && <Badge color="red">Low confidence</Badge>}
                  </SpaceBetween>
                ),
            },
            {
              id: 'segments',
              header: 'Segments',
              cell: (h) => (h.invalid ? '-' : `${plural(h.segments, 'segment')}${h.differs ? ', values differ' : ''}`),
            },
          ]}
        />
      )}
      {summary.rollups.length > 0 && (
        <Table
          header={<Header variant="h3">Rollups</Header>}
          ariaLabels={{ tableLabel: 'Rollups' }}
          items={summary.rollups}
          trackBy="id"
          columnDefinitions={[
            { id: 'name', header: 'Rollup', isRowHeader: true, cell: (r) => r.name },
            {
              id: 'value',
              header: 'Value',
              cell: (r) =>
                r.invalid ? (
                  <StatusIndicator type="error">{MISSING_FIELD_MESSAGE}</StatusIndicator>
                ) : (
                  <SpaceBetween size="xxs">
                    <span>{showNumber(r.value)}</span>
                    {r.groups && r.groups.length > 0 && (
                      <ul className="value-list" aria-label={`Groups of ${r.name}`}>
                        {r.groups.map((g) => (
                          <li key={g.group}>
                            {g.group === '' ? '(no value)' : memberName(g.group)}: {showNumber(g.value)} (
                            {plural(g.used, 'value')})
                          </li>
                        ))}
                      </ul>
                    )}
                  </SpaceBetween>
                ),
            },
            { id: 'used', header: 'Values used', cell: (r) => (r.invalid ? '-' : r.used) },
          ]}
        />
      )}
      {summary.checks.length > 0 && (
        <Table
          header={<Header variant="h3">Checks</Header>}
          ariaLabels={{ tableLabel: 'Checks' }}
          items={summary.checks}
          trackBy="id"
          columnDefinitions={[
            { id: 'name', header: 'Check', isRowHeader: true, cell: (c) => c.name },
            { id: 'left', header: 'Left', cell: (c) => showNumber(c.left) },
            { id: 'right', header: 'Right', cell: (c) => showNumber(c.right) },
            { id: 'difference', header: 'Difference', cell: (c) => showNumber(c.difference) },
            {
              id: 'status',
              header: 'Status',
              cell: (c) =>
                c.invalid ? (
                  <StatusIndicator type="error">{MISSING_FIELD_MESSAGE}</StatusIndicator>
                ) : (
                  <SpaceBetween size="xxs">
                    <StatusIndicator type={CHECK_STATUS[c.status].type}>{CHECK_STATUS[c.status].text}</StatusIndicator>
                    {inputLinks(c.inputs)}
                  </SpaceBetween>
                ),
            },
          ]}
        />
      )}
      <SpaceBetween size="xxs">
        <Box>
          {plural(summary.noMatchPages, 'page')} had no matching blueprint ({plural(summary.noMatchSegments, 'segment')}
          ).
        </Box>
        {summary.possibleMismatchSegments > 0 && (
          <Box>
            {plural(summary.possibleMismatchSegments, 'segment')} ({plural(summary.possibleMismatchPages, 'page')}) may
            use the wrong blueprint and {summary.possibleMismatchSegments === 1 ? 'is' : 'are'} left out.
          </Box>
        )}
      </SpaceBetween>
    </SpaceBetween>
  );
}

// ---------- No_Match and Possible_Mismatch tabs ----------

/** The Segment's standard-output text from `GET /runs/:id/segments/:index/text` (Req 5.4, 5.10). */
function SegmentText({ runId, index }: { runId: string; index: number }) {
  const q = useQuery({
    queryKey: ['segmentText', runId, index],
    queryFn: () => api.getSegmentText(runId, index),
    retry: false,
    staleTime: Infinity,
  });
  if (q.isPending) return <Spinner />;
  if (q.isError) {
    if (q.error instanceof ApiError && q.error.status === 404)
      return <Box color="text-body-secondary">{NO_SEGMENT_TEXT}</Box>;
    return (
      <Alert type="error">
        <ErrorDetail error={q.error} />
      </Alert>
    );
  }
  if (q.data.text.trim() === '') return <Box color="text-body-secondary">{NO_SEGMENT_TEXT}</Box>;
  return (
    <SpaceBetween size="xs">
      <Box variant="h3">Text BDA read from these pages</Box>
      <div
        role="region"
        aria-label="Segment text"
        tabIndex={0}
        style={{ maxHeight: '24rem', overflow: 'auto', whiteSpace: 'pre-wrap' }}
      >
        <Box variant="code">{q.data.text}</Box>
      </div>
      {q.data.truncated && <Box color="text-body-secondary">The text was shortened.</Box>}
    </SpaceBetween>
  );
}

/** "Create a blueprint from these pages" (PDF) or "… from this document"; navigates to the Editor's sample flow. */
function CreateBlueprintButton({
  runId,
  index,
  isPdf,
  onCreateBlueprint,
}: {
  runId: string;
  index: number;
  isPdf: boolean;
  onCreateBlueprint?: (segmentIndex: number) => Promise<void> | void;
}) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const create = async () => {
    setBusy(true);
    setError(null);
    try {
      if (onCreateBlueprint) await onCreateBlueprint(index);
      else if (isPdf) navigate(sampleEditorPath(await createBlueprintFromSegment(runId, index)));
      else navigate('/blueprints/new');
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };
  return (
    <SpaceBetween size="xs">
      <Button iconName="add-plus" loading={busy} onClick={() => void create()}>
        {isPdf ? CREATE_FROM_PAGES : CREATE_FROM_DOCUMENT}
      </Button>
      {error !== null && (
        <Alert type="error">
          <ErrorDetail error={error} />
        </Alert>
      )}
    </SpaceBetween>
  );
}

const ShowPage = ({ segment, onShowPage }: { segment: NormalizedSegment; onShowPage?: (page: number) => void }) =>
  onShowPage ? (
    <Button
      iconName="file-open"
      onClick={() => onShowPage(segment.pageStart)}
      ariaLabel={`Show page ${segment.pageStart} in the document`}
    >
      Show page {segment.pageStart}
    </Button>
  ) : null;

function NoMatchPanel({
  runId,
  segment,
  isPdf,
  onShowPage,
  onCreateBlueprint,
}: {
  runId: string;
  segment: NormalizedSegment;
  isPdf: boolean;
  onShowPage?: (page: number) => void;
  onCreateBlueprint?: (segmentIndex: number) => Promise<void> | void;
}) {
  return (
    <SpaceBetween size="l">
      <ShowPage segment={segment} onShowPage={onShowPage} />
      <Alert type="info" header={NO_MATCH_LABEL}>
        BDA did not match any blueprint of this set to {segmentLabel(segment).toLowerCase()}, so no fields were
        extracted.
      </Alert>
      <CreateBlueprintButton runId={runId} index={segment.index} isPdf={isPdf} onCreateBlueprint={onCreateBlueprint} />
      <SegmentText runId={runId} index={segment.index} />
    </SpaceBetween>
  );
}

function MismatchPanel({
  runId,
  segment,
  member,
  accepted,
  onAccept,
  threshold,
  allowed,
  isPdf,
  onShowPage,
  onCreateBlueprint,
}: {
  runId: string;
  segment: NormalizedSegment;
  member: Member;
  accepted: boolean;
  onAccept: (on: boolean) => void;
  threshold: number;
  allowed?: AllowedValues;
  isPdf: boolean;
  onShowPage?: (page: number) => void;
  onCreateBlueprint?: (segmentIndex: number) => Promise<void> | void;
}) {
  const c = segment.fieldCoverage!;
  return (
    <SpaceBetween size="l">
      <Alert
        type={accepted ? 'info' : 'warning'}
        header={accepted ? 'Match accepted for this view' : POSSIBLE_MISMATCH_LABEL}
      >
        <SpaceBetween size="s">
          <span>
            BDA used {member.name} for these pages, but found only {c.found} of {plural(c.total, 'field')}. The pages
            may be a document type this set has no blueprint for.
          </span>
          <Toggle
            checked={accepted}
            onChange={(e) => onAccept(e.detail.checked)}
            description="Counts this segment in the packet summary and the downloads of this view. Not saved."
          >
            Use this match
          </Toggle>
        </SpaceBetween>
      </Alert>
      <SegmentPanel segment={segment} threshold={threshold} onShowPage={onShowPage} allowed={allowed} />
      <CreateBlueprintButton runId={runId} index={segment.index} isPdf={isPdf} onCreateBlueprint={onCreateBlueprint} />
      <SegmentText runId={runId} index={segment.index} />
    </SpaceBetween>
  );
}
